import React, { useState, useRef, useEffect } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import DOMPurify from "dompurify";
import DataViewIcon from "./DataViewIcons";
import SectionHeading from "../../components/SectionHeading";
import ActionIcon from "../../assets/icons/action-icon.svg";
import Pagination from "../../components/table/Pagination";
import { Breadcrumb } from "../../components/ui/Breadcrumb";
import {
    useLazyGetReportDetailsQuery,
    useDeleteReportViewMutation,
    useUpdateReportDetailsMutation,
    useSaveReportViewMutation,
} from "../../slices/saveReportViewSlice";
import {
    useSaveReportViewReminderMutation,
    useUpdateReportViewReminderMutation,
    useDeleteReportViewReminderMutation,
} from "../../slices/saveReportViewReminderSlice";
import { useSelector, useDispatch } from "react-redux";
import ReportDeletePage from "./ReportDeletePage";
import ShareReportDialogBox from "./ShareReportDialogBox";
import ReportSaveNotification from "./ReportSaveNotification";
import Loader from "../../components/ui/Loader";
import SectionSubheading from "../../components/SectionSubheading";
import SavedReportEditDialogBox from "./SavedReportEditDialogBox";
import GTM from "../../GTM";
import useOutsideClick from "../../hooks/useOutsideClick";
import ManageReminderDialogBox from "./ManageReminderDialogBox";
import { useFlags } from "launchdarkly-react-client-sdk";
import {
    buildGtmOrgContext,
    filterChangesDataViews,
    filterDocumentDataViews,
    filterSelectedReport,
    getIsDocumentLibraryTileEnabled,
    getIsNewLeaveChangesEnabled,
    getIsOldChangesTileEnabled,
    getSelectedIsfOrganizationIds,
    getSubViewNames,
    isParameterEnabled,
} from "../../utils/helpers";
import {
    DATAVIEWTYPE,
    dataViewTypeOrder,
    INCLUDE_HISTORICAL_LEAVE,
    OWNER_TYPE,
    ownerTypeOrder,
    paymentHistoryType,
    PERIOD_SCHEDULE_TYPE,
    PLAN_REQUEST_STATUS,
    POLICIES_AND_DIVISIONS,
    PROTECTION_STATUS,
    REPORTING_GROUP,
    REPORTING_PERIOD,
    PAYMENT_STATUS,
} from "../../constants";
import {
    setReportDetails,
    setSelectedDataViewType,
    setSelectedReportViewId,
} from "../../slices/viewReportSlice";
import RequestFailed from "../../components/alerts/RequestFailed";
import PropTypes from "prop-types";

const Popup = ({
    onClose,
    onEdit,
    onDelete,
    viewData,
    onManageReminder,
    displayManageReminder,
    onShare,
}) => {
    const popupRef = useRef(null);
    const dropdownRef = useRef(null);
    const { user } = useSelector((state) => state.auth);
    const { displayShareReports } = useFlags();
    const isClickedOutside = useOutsideClick(popupRef, dropdownRef);

    useEffect(() => {
        if (isClickedOutside) {
            onClose();
        }
    }, [isClickedOutside, onClose]);

    const sendDeleteEvent = () => {
        onDelete(viewData);
    };

    const sendEditEvent = () => {
        onEdit(viewData);
    };

    const sendManageReminderEvent = () => {
        onManageReminder(viewData);
    };

    const sendShareEvent = () => {
        onShare(viewData);
    };

    return (
        <div ref={dropdownRef}>
            {" "}
            <div className="section-card action-popup" ref={popupRef}>
                {viewData.OwnerType !== "Standard" && (
                    <div className="cursor-pointer" onClick={sendEditEvent}>
                        Edit name/description
                    </div>
                )}
                {((user.isUserInternal && viewData.OwnerType === "Shared") ||
                    viewData.OwnerType === "Personal") && (
                    <div className="cursor-pointer" onClick={sendDeleteEvent}>
                        Delete report
                    </div>
                )}
                {displayManageReminder && !user.isUserInternal && (
                    <div
                        className="cursor-pointer"
                        onClick={sendManageReminderEvent}
                    >
                        Manage reminder
                    </div>
                )}
                {displayShareReports &&
                    viewData.OwnerType === "Personal" &&
                    !viewData.Shared && (
                        <div
                            className="cursor-pointer"
                            onClick={sendShareEvent}
                        >
                            Share
                        </div>
                    )}
            </div>
        </div>
    );
};

function getReminderConfiguration({
    selectedFrequencyPattern,
    selectedRecurrencePattern,
    daysOfWeeks,
    daysOfMonth,
    manageSubscription,
}) {
    let recurrancePattern = null;
    let daysOfTheWeek = null;
    let monthsOfTheYear = null;
    let dayOfTheMonth = null;
    let disableSaveButton = true;

    if (selectedFrequencyPattern === "Daily") {
        return handleDaily(selectedRecurrencePattern);
    }

    if (selectedFrequencyPattern === "Weekly") {
        return handleWeekly(daysOfWeeks);
    }

    if (selectedFrequencyPattern === "Monthly") {
        return handleMonthly(
            daysOfMonth,
            manageSubscription,
            selectedFrequencyPattern
        );
    }

    return {
        recurrancePattern,
        daysOfTheWeek,
        monthsOfTheYear,
        dayOfTheMonth,
        disableSaveButton,
    };
}

function handleDaily(selectedRecurrencePattern) {
    const recurrancePattern =
        selectedRecurrencePattern === "Everyday" ? "Daily" : "WorkWeek";
    const dayOfTheMonth = -1;
    const disableSaveButton =
        selectedRecurrencePattern === "Everyday" ||
        selectedRecurrencePattern === "Every weekday"
            ? false
            : true;

    return {
        recurrancePattern,
        daysOfTheWeek: null,
        monthsOfTheYear: null,
        dayOfTheMonth,
        disableSaveButton,
    };
}

function handleWeekly(daysOfWeeks) {
    const dayOfTheMonth = -1;
    const disableSaveButton = daysOfWeeks.length > 0 ? false : true;

    return {
        recurrancePattern: null,
        daysOfTheWeek: daysOfWeeks,
        monthsOfTheYear: null,
        dayOfTheMonth,
        disableSaveButton,
    };
}

function handleMonthly(
    daysOfMonth,
    manageSubscription,
    selectedFrequencyPattern
) {
    const hasSelectedMonths = daysOfMonth.selectedMonths?.length > 0;
    const recurrancePattern = hasSelectedMonths
        ? "SelectMonths"
        : selectedFrequencyPattern;
    const monthsOfTheYear = hasSelectedMonths
        ? daysOfMonth.selectedMonths
        : null;
    const dayOfTheMonth = daysOfMonth.selectedDates;

    let disableSaveButton = true;

    if (daysOfMonth.selectedMonthly === "selectMonths") {
        disableSaveButton = shouldDisableSelectMonths(
            daysOfMonth,
            manageSubscription
        );
    }

    if (daysOfMonth.selectedMonthly === "everyMonth") {
        disableSaveButton = shouldDisableEveryMonth(
            daysOfMonth,
            manageSubscription
        );
    }

    return {
        recurrancePattern,
        daysOfTheWeek: null,
        monthsOfTheYear,
        dayOfTheMonth,
        disableSaveButton,
    };
}

function shouldDisableSelectMonths(daysOfMonth, manageSubscription) {
    return !(
        (daysOfMonth.selectedDates !== undefined &&
            daysOfMonth.selectedMonths.length > 0) ||
        (manageSubscription?.dayOfTheMonth !== undefined &&
            manageSubscription?.monthsOfTheYear.length < 12)
    );
}

function shouldDisableEveryMonth(daysOfMonth, manageSubscription) {
    return !(
        daysOfMonth.selectedDates !== undefined ||
        (manageSubscription?.dayOfTheMonth !== undefined &&
            manageSubscription.recurrancePattern === "Monthly")
    );
}

const ManageMyView = () => {
    const gtm = new GTM(null);
    const { viewType } = useParams();
    const dispatch = useDispatch();
    const [
        getReportDetails,
        { data: savedReportNames, isError: isReportDetalisError },
    ] = useLazyGetReportDetailsQuery({});
    const [selectedAction, setSelectedAction] = useState(null);
    const [tableItems, setTableItems] = useState(50);
    const [pageNumber, setPageNumber] = useState(0);
    const locationReports = useLocation().state?.data;
    const [tableData, setTableData] = useState(locationReports || {});
    const usersPerPage = tableItems || 5;
    const [show, setShow] = useState(false);
    const [selectedView, setSelectedView] = useState(null);
    const [showUpdatePage, setShowUpdatePage] = useState(false);
    const [showManageReminder, setShowManageReminder] = useState(false);
    const {
        displayManageReminder,
        displayReports,
        displaySubViewReports,
        displayDocumentLibraryTile,
        displayoldChangesTile,
        displayNewLeaveChangesReport,
    } = useFlags();
    const {
        enableExternalUser,
        enableInternalUser,
        enableOrganizationList,
        isfUserIds,
        subViewNames,
    } = displaySubViewReports;

    const { organizations: allOrganizations } = useSelector(
        (state) => state.userDetails
    );

    const [showFrequency, setShowFrequency] = useState(false);
    const [showShareReportDialogBox, setShowShareReportDialogBox] =
        useState(false);
    const {
        selectedOrganizationsReports: userSelectedOrganizationReports,
        selectedOrganizationsIdReports: reportId,
        selectedRelatedOrganizationsForReports: selectedRelatedOrganizations,
    } = useSelector((state) => state.reportOrganization);
    const [showReportSaveNotification, setShowReportSaveNotification] =
        useState(false);
    const [showReportUpdatedNotification, setShowReportUpdatedNotification] =
        useState(false);
    const [showSetReminderNotification, setShowSetReminderNotification] =
        useState(false);
    const [showDeleteReminderNotification, setShowDeleteReminderNotification] =
        useState(false);
    const [showUpdateReminderNotification, setShowUpdateReminderNotification] =
        useState(false);
    const [showShareReportNotification, setShowShareReportNotification] =
        useState(false);
    const [showLoader, setShowLoader] = useState(false);
    const [changedViewName, setChangedViewName] = useState();
    const [changedViewDescription, setChangedViewDescription] = useState();
    const [updateReportViewMutation] = useUpdateReportDetailsMutation();
    const [actionType, setActiontype] = useState("Searching...");
    const [manageReminderReportName, setManageReminderReportName] = useState();
    const [manageSubscription, setManageSubscription] = useState();
    const [selectedFrequencyPattern, setSelectedFrequencyPattern] = useState();
    const [selectedReminderCheck, setSelectedReminderCheck] = useState(false);
    const [selectedSubscriptionId, setSelectedSubscriptionId] = useState(null);
    const [daysOfWeeks, setDaysOfWeeks] = useState([]);
    const [daysOfMonth, setDaysOfMonth] = useState([]);
    const [selectedRecurrencePattern, setSelectedRecurrencePattern] =
        useState();
    const [saveReportViewReminderMutation] =
        useSaveReportViewReminderMutation();
    const [updateReportViewReminderMutation] =
        useUpdateReportViewReminderMutation();
    const [deleteReportViewReminderMutation] =
        useDeleteReportViewReminderMutation();
    const [deleteReportViewMutation] = useDeleteReportViewMutation();
    // setting SubscriptionInfo

    const navigate = useNavigate();

    const { totalParentOrganizations } = useSelector(
        (state) => state.organizations
    );
    const { selectedIsfOrganizationIds, organizationGroupId } =
        getSelectedIsfOrganizationIds(
            userSelectedOrganizationReports,
            reportId,
            selectedRelatedOrganizations
        );

    const relatedOrgNames = Array.from(selectedRelatedOrganizations).map(
        (relatedOrg) => relatedOrg.label
    );
    const sortedRelatedOrgNames = [...relatedOrgNames].sort((left, right) =>
        left.localeCompare(right)
    );
    const sortedRelatedOrgCSV = sortedRelatedOrgNames.join(","); // Then join the sorted array into a string

    const formattedRelatedOrgCSV =
        sortedRelatedOrgCSV.length > 30
            ? sortedRelatedOrgCSV.substring(0, 30) + "..."
            : sortedRelatedOrgCSV;
    const [saveReportViewMutation] = useSaveReportViewMutation();
    const { user } = useSelector((state) => state.auth);
    const reportDetailsParams = {
        isfUserId: user.userId,
        dataViewType: DATAVIEWTYPE,
        selectedIsfOrganizationIds: selectedIsfOrganizationIds,
    };

    if (organizationGroupId) {
        reportDetailsParams.organizationGroupId = organizationGroupId;
    }

    const filterResult = getSubViewNames({
        enableExternalUser,
        enableInternalUser,
        enableOrganizationList,
        isfUserIds,
        subViewNames,
        user,
    });

    const isDocumentLibraryTileEnabled = getIsDocumentLibraryTileEnabled({
        displayDocumentLibraryTile,
        user,
        allOrganizations,
    });

    const isOldChangesTileEnabled = getIsOldChangesTileEnabled({
        displayoldChangesTile,
        user,
        allOrganizations,
    });

    const isNewLeaveChangesEnabled = getIsNewLeaveChangesEnabled({
        displayNewLeaveChangesReport,
        user,
        allOrganizations,
    });

    useEffect(() => {
        if (tableData.length === 0) {
            getReportDetails(reportDetailsParams);
        }
    }, []);

    useEffect(() => {
        const sourceReports =
            savedReportNames ??
            (locationReports?.length ? locationReports : undefined);
        if (sourceReports) {
            const filteredReports = filterChangesDataViews(
                filterDocumentDataViews(
                    filterSelectedReport(
                        sourceReports,
                        subViewNames,
                        filterResult
                    ),
                    isDocumentLibraryTileEnabled
                ),
                isOldChangesTileEnabled,
                isNewLeaveChangesEnabled
            );
            dispatch(setReportDetails(filteredReports));
            setTableData(filteredReports);
        }
    }, [
        savedReportNames,
        locationReports,
        isDocumentLibraryTileEnabled,
        isOldChangesTileEnabled,
        isNewLeaveChangesEnabled,
    ]);

    const getPageCount = () => {
        return Math.ceil(tableData.length / usersPerPage);
    };
    const handleItemsPerPageChange = (e) => {
        setTableItems(e);
    };
    const pagesVisited = pageNumber * usersPerPage;
    const changePage = (selected) => {
        setPageNumber(selected);
    };

    const handelGtmTags = (
        category,
        action,
        label,
        attribute1,
        attribute2,
        attribute3,
        attribute4
    ) => {
        gtm.Event(
            "manage my reports",
            category ? category : "button",
            action ? action : "click",
            label,
            attribute1 ? attribute1 : "manage my reports",
            attribute2 ? attribute2 : "action",
            attribute3 ? attribute3 : "field_interaction",
            attribute4 ? attribute4 : ""
        );
    };

    const { config } = useSelector((state) => state.app);

    const page_referrer_title = null;
    const { id } = useParams();

    useEffect(() => {
        const userEmail = user.userEmail || "";
        const userId = user ? user.userId : "";
        const userRole = user.isUserInternal ? "internal" : "external";
        const { employerName, relatedOrgNames, organizationGroupId } =
            buildGtmOrgContext(userSelectedOrganizationReports, reportId);

        gtm.PageView(
            "manage my reports",
            employerName,
            userId,
            userRole,
            window.location.href,
            `${config.REACT_APP_BASE_URL}${page_referrer_title}`,
            page_referrer_title,
            id,
            userEmail,
            relatedOrgNames,
            organizationGroupId,
            displayReports
        );
    }, [20000]);

    const handleActionClick = (index) => {
        setSelectedAction(index);
        handelGtmTags("button", "click", "ellipsis");
    };

    const handleEdit = (item) => {
        setSelectedView(item);
        setSelectedAction(null);
        setShowUpdatePage(true);

        setChangedViewName(item.Name);
        setChangedViewDescription(item.Description);
        handelGtmTags(
            "button",
            "click",
            "ellipsis",
            "manage my reports",
            "action",
            "field_interaction",
            "edit name/description"
        );
    };

    const handleDelete = (item) => {
        setShow(true);
        setSelectedView(item);
        setSelectedAction(null);
        handelGtmTags(
            "button",
            "click",
            "ellipsis",
            "manage my reports",
            "action",
            "field_interaction",
            "delete report"
        );
    };

    const handleShareReport = (item) => {
        setSelectedView(item);
        setShowShareReportDialogBox(true);
        setSelectedAction(null);
        handelGtmTags(
            "button",
            "click",
            "ellipsis",
            "manage my reports",
            "action",
            "field_interaction",
            "share"
        );
    };

    const handleManageReminder = (item) => {
        setShowManageReminder(true);
        setSelectedAction(null);
        setManageReminderReportName(item.Name);
        if (item.SubscriptionInfo) {
            setManageSubscription(item.SubscriptionInfo);
            setShowFrequency(true);
            setSelectedReminderCheck(true);
            setSelectedFrequencyPattern(item.SubscriptionInfo.frequency);
            if (item.SubscriptionInfo.frequency === "Daily") {
                setSelectedRecurrencePattern(
                    item.SubscriptionInfo.recurrancePattern === "Daily"
                        ? "Everyday"
                        : "Every weekday"
                );
            } else if (item.SubscriptionInfo.frequency === "Weekly") {
                setSelectedRecurrencePattern("Weekly");
            } else if (item.SubscriptionInfo.frequency === "Monthly") {
                const recurrancePatterns =
                    item.SubscriptionInfo.recurrancePattern === "Monthly"
                        ? "everyMonth"
                        : "selectMonths";
                const selectedItems = {
                    selectedDates: item.SubscriptionInfo.dayOfTheMonth,
                    selectedMonths: item.SubscriptionInfo.monthsOfTheYear,
                    selectedMonthly: recurrancePatterns,
                };
                setDaysOfMonth(selectedItems);
                setSelectedRecurrencePattern(
                    item.SubscriptionInfo.recurrancePattern === "Monthly"
                        ? "everyMonth"
                        : "selectMonths"
                );
            }
            setSelectedSubscriptionId(item.SubscriptionInfo.subscriptionId);
        }
        if (!item.SubscriptionInfo) {
            setSelectedReminderCheck(false);
        }
        setSelectedView(item);
        handelGtmTags(
            "button",
            "click",
            "ellipsis",
            "manage my reports",
            "action",
            "field_interaction",
            "manage reminder"
        );
    };

    const autoHideReportNotification = () => {
        setTimeout(() => {
            setShowReportSaveNotification(false);
        }, 6000);
    };

    const handleReportViewDelete = () => {
        setActiontype("Delete");
        setShowLoader(true);
        deleteReportViewMutation({
            dataViewType: {
                viewIds: [selectedView.ViewId],
            },
        }).then(() => {
            getReportDetails(reportDetailsParams).then(() => {
                setShowLoader(false);
                setShowReportSaveNotification(true);
                autoHideReportNotification();
            });
        });
        setShow(false);
    };

    const handleReportViewCancelDelete = () => {
        setShow(false);
        setShowShareReportDialogBox(false);
        handelGtmTags(
            "button",
            "click",
            "cancel",
            "manage my views",
            "share modal",
            "field_interaction",
            "undefined"
        );
    };

    const handleReportSavedNotificationClose = () => {
        setShowReportSaveNotification(false);
    };

    const handleReportUpdatedNotificationClose = () => {
        setShowReportUpdatedNotification(false);
    };

    const handleSetReminderNotificationClose = () => {
        setShowSetReminderNotification(false);
    };

    const handleDeleteReminderNotificationClose = () => {
        setShowDeleteReminderNotification(false);
    };

    const handleUpdateReminderNotificationClose = () => {
        setShowUpdateReminderNotification(false);
    };
    const handleShareReportNotificationClose = () => {
        setShowShareReportNotification(false);
    };

    const handleReportViewDontSave = () => {
        setShow(false);
        setShowUpdatePage(false);
        setShowManageReminder(false);
        setShowFrequency(false);
        setSelectedRecurrencePattern(null);
        setDaysOfMonth([]);
        setSelectedReminderCheck(false);
        setSelectedFrequencyPattern(null);
        setManageSubscription(undefined);
        setSelectedSubscriptionId(null);
        handelGtmTags(
            "button",
            "click",
            "cancel",
            "manage my views",
            "subscribe modal",
            "field_interaction",
            "undefined"
        );
    };

    const [saveUpdateReportParameters, setSaveUpdateReportParameters] =
        useState({});

    useEffect(() => {
        if (selectedView !== null) {
            // Determine if each filter is enabled
            const enablePaymentStatus = isParameterEnabled(
                selectedView?.ParametersOptions,
                PAYMENT_STATUS
            );
            const enablePeriodScheduleTypeFilter = isParameterEnabled(
                selectedView?.ParametersOptions,
                PERIOD_SCHEDULE_TYPE
            );
            const enablePeriodStatusFilter = isParameterEnabled(
                selectedView?.ParametersOptions,
                PLAN_REQUEST_STATUS
            );
            const enableProtectionStatusFilter = isParameterEnabled(
                selectedView?.ParametersOptions,
                PROTECTION_STATUS
            );
            const enableIncludeHistoricalLeave = isParameterEnabled(
                selectedView?.ParametersOptions,
                INCLUDE_HISTORICAL_LEAVE
            );
            const enableReportingPeriodFilter = isParameterEnabled(
                selectedView?.ParametersOptions,
                REPORTING_PERIOD
            );
            const enableReportGroupFilter = isParameterEnabled(
                selectedView?.ParametersOptions,
                REPORTING_GROUP
            );
            const enablePolicyandDivisisonFilter = isParameterEnabled(
                selectedView?.ParametersOptions,
                POLICIES_AND_DIVISIONS
            );

            const parameters = {
                coverageTypes: selectedView?.Parameters?.CoverageType,
                effectiveStartDt: selectedView?.Parameters?.EffectiveStartDate
                    ? selectedView?.Parameters?.EffectiveStartDate
                    : null,
                effectiveEndDt: selectedView?.Parameters?.EffectiveEndDate
                    ? selectedView?.Parameters?.EffectiveEndDate
                    : null,
                ...(enablePolicyandDivisisonFilter && {
                    policyAndDivisions:
                        selectedView?.Parameters?.PolicyAndDivisions,
                }),
                ...(enableIncludeHistoricalLeave && {
                    includeHistoricalLeave:
                        selectedView?.Parameters?.IncludeHistoricalLeave,
                }),
                ...(enablePaymentStatus && {
                    paymentStatuses: selectedView?.Parameters?.PaymentStatuses,
                }),
                ...(enablePeriodScheduleTypeFilter && {
                    periodScheduleTypes:
                        selectedView?.Parameters?.PeriodScheduleTypes,
                }),
                ...(enablePeriodStatusFilter && {
                    planRequestStatus:
                        selectedView?.Parameters?.planRequestStatus,
                }),
                ...(enableProtectionStatusFilter && {
                    protectionStatuses:
                        selectedView?.Parameters?.ProtectionStatuses,
                }),
                ...(enableReportGroupFilter && {
                    reportingGroups: selectedView?.Parameters?.ReportingGroups,
                }),
                ...(enableReportingPeriodFilter
                    ? {
                          reportingPeriod:
                              selectedView?.Parameters?.reportingPeriod,
                      }
                    : { reportingPeriod: "" }),
                ...(selectedView.DataViewType !== "PaymentHistory" && {
                    statuses: selectedView?.Parameters?.Status,
                }),
            };
            // Update the state with the constructed report parameters
            setSaveUpdateReportParameters(parameters);
        }
    }, [selectedView]);

    const handleReportUpdatedNotification = () => {
        setTimeout(() => {
            setShowReportUpdatedNotification(false);
        }, 6000);
    };

    const handleReportViewUpdate = (viewNameValue, descriptionValue) => {
        setActiontype("Update");
        setShowUpdatePage(false);
        setShowLoader(true);
        updateReportViewMutation({
            dataToUpdate: {
                isfUserId: user.userId,
                ownerType: selectedView.OwnerType,
                selectedIsfOrganizationIds: selectedIsfOrganizationIds,
                dataViewType: selectedView.DataViewType,
                subViewName: selectedView.SubViewName,
                isEnabled: true,
                dataViewInfo: {
                    name: viewNameValue ? viewNameValue : changedViewName,
                    description: descriptionValue
                        ? descriptionValue
                        : changedViewDescription,
                    parameters: saveUpdateReportParameters,
                    tableDisplayInformation: {
                        sortOrder:
                            selectedView.TableDisplayInformation.SortOrder,
                        columnInformation:
                            selectedView.TableDisplayInformation.ColumnInformation.map(
                                (item) => {
                                    return {
                                        displayOrder: item.displayOrder,
                                        checked: item.checked,
                                        size: item.size,
                                        columnId: item.columnId,
                                    };
                                }
                            ),
                    },
                },
            },
            viewId: selectedView.ViewId,
        }).then(() => {
            getReportDetails(reportDetailsParams).then(() => {
                setShowLoader(false);
                setShowReportUpdatedNotification(true);
                handleReportUpdatedNotification();
            });
        });
    };

    const handleViewNameChange = (item) => {
        setChangedViewName(item);
    };

    const handleDescriptionChange = (item) => {
        setChangedViewDescription(item);
    };

    const handleReminderCheck = (item) => {
        setShowFrequency(item);
        if (!item) {
            setSelectedReminderCheck(false);
            handelGtmTags(
                "checkbox",
                "uncheck",
                "subscribe to receive notifiications/emails",
                "manage my views",
                "subscribe modal",
                "field_interaction",
                "undefined"
            );
        } else if (item) {
            setSelectedReminderCheck(true);
            handelGtmTags(
                "checkbox",
                "check",
                "subscribe to receive notifiications/emails",
                "manage my views",
                "subscribe modal",
                "field_interaction",
                "undefined"
            );
        }
    };

    const handleFrequencyPatternClick = (item) => {
        setSelectedFrequencyPattern(item);
        handelGtmTags(
            "button",
            "click",
            "frequency",
            "manage my views",
            "subscribe modal",
            "field_interaction",
            item
        );
    };

    const handleRecurrencePatternClick = (item) => {
        setSelectedRecurrencePattern(item);
        handelGtmTags(
            "button",
            "click",
            "daily recurrence pattern",
            "manage my views",
            "subscribe modal",
            "field_interaction",
            item
        );
    };

    const handleDaysOfTheWeek = (weeks) => {
        setDaysOfWeeks(weeks);
        handelGtmTags(
            "button",
            "click",
            "weekly recurrence days",
            "manage my views",
            "subscribe modal",
            "field_interaction",
            weeks
        );
    };

    const handleMonthly = ({
        selectedDates,
        selectedMonths,
        selectedMonthly,
    }) => {
        setDaysOfMonth({
            selectedDates: selectedDates,
            selectedMonths: selectedMonths,
            selectedMonthly: selectedMonthly,
        });
        if (
            selectedMonthly === "everyMonth" ||
            selectedMonthly === "selectMonths"
        ) {
            handelGtmTags(
                "button",
                "click",
                "monthly recurrence pattern",
                "manage my views",
                "subscribe modal",
                "field_interaction",
                selectedMonthly
            );
        }
        if (selectedDates) {
            handelGtmTags(
                "button",
                "click",
                "monthly recurrence date",
                "manage my views",
                "subscribe modal",
                "field_interaction",
                selectedDates
            );
        }
        if (selectedMonths.length > 0) {
            handelGtmTags(
                "button",
                "click",
                "monthly recurrence month",
                "manage my views",
                "subscribe modal",
                "field_interaction",
                selectedMonths
            );
        }
    };

    const getReportDetailsForRefresh = () => {
        getReportDetails(reportDetailsParams);
    };

    const {
        recurrancePattern,
        daysOfTheWeek,
        monthsOfTheYear,
        dayOfTheMonth,
        disableSaveButton,
    } = getReminderConfiguration({
        selectedFrequencyPattern,
        selectedRecurrencePattern,
        daysOfWeeks,
        daysOfMonth,
        manageSubscription,
    });

    const handleSetReminder = () => {
        setShowManageReminder(false);
        setShowLoader(true);

        if (selectedSubscriptionId !== null && !selectedReminderCheck) {
            setActiontype("Delete");
            deleteReportViewReminderMutation({
                dataToUpdate: {
                    SubscriptionId: selectedSubscriptionId,
                },
                viewId: selectedView.ViewId,
            })
                .unwrap()
                .then(() => {
                    getReportDetailsForRefresh();
                    setShowLoader(false);
                    setSelectedRecurrencePattern(undefined);
                    setShowFrequency(false);
                    setDaysOfMonth([]);
                    setShowDeleteReminderNotification(true);
                    setSelectedFrequencyPattern(undefined);
                    setManageSubscription(undefined);
                    setSelectedSubscriptionId(null);
                    setTimeout(() => {
                        setShowDeleteReminderNotification(false);
                    }, 6000);
                    handelGtmTags(
                        "button",
                        "click",
                        "delete",
                        "manage my views",
                        "subscribe modal",
                        "unsubscribe_to_view",
                        selectedFrequencyPattern
                    );
                })
                .catch((e) => {
                    setShowLoader(false);
                    setSelectedRecurrencePattern("");
                    setShowFrequency(false);
                });
        } else if (selectedSubscriptionId !== null && selectedReminderCheck) {
            setActiontype("Update");
            updateReportViewReminderMutation({
                dataToUpdate: {
                    frequency: selectedFrequencyPattern,
                    recurrancePattern: recurrancePattern,
                    daysOfTheWeek: daysOfTheWeek,
                    monthsOfTheYear: monthsOfTheYear,
                    dayOfTheMonth: dayOfTheMonth,
                },
                viewId: selectedView.ViewId,
                SubscriptionId: selectedSubscriptionId,
            })
                .unwrap()
                .then(() => {
                    getReportDetailsForRefresh();
                    setShowLoader(false);
                    setSelectedRecurrencePattern(undefined);
                    setShowFrequency(false);
                    setDaysOfMonth([]);
                    setShowUpdateReminderNotification(true);
                    setSelectedFrequencyPattern(undefined);
                    setManageSubscription(undefined);
                    setSelectedSubscriptionId(null);
                    setTimeout(() => {
                        setShowUpdateReminderNotification(false);
                    }, 6000);
                    handelGtmTags(
                        "button",
                        "click",
                        "update",
                        "manage my views",
                        "subscribe modal",
                        "update_to_view",
                        selectedFrequencyPattern
                    );
                })
                .catch((e) => {
                    setShowLoader(false);
                    setSelectedRecurrencePattern("");
                    setShowFrequency(false);
                    setDaysOfMonth([]);
                    setSelectedFrequencyPattern(undefined);
                });
        } else if (!selectedSubscriptionId) {
            setActiontype("Set");
            saveReportViewReminderMutation({
                dataToUpdate: {
                    isfUserId: user.userId,
                    selectedIsfOrganizationIds: selectedIsfOrganizationIds,
                    emailAddress: user.userEmail,
                    internalUser: user.isUserInternal,
                    subscriptionInfo: {
                        frequency: selectedFrequencyPattern,
                        recurrancePattern: recurrancePattern,
                        daysOfTheWeek: daysOfTheWeek,
                        monthsOfTheYear: monthsOfTheYear,
                        dayOfTheMonth: dayOfTheMonth,
                    },
                },
                viewId: selectedView.ViewId,
            })
                .unwrap()
                .then(() => {
                    getReportDetailsForRefresh();
                    setShowLoader(false);
                    setSelectedRecurrencePattern(undefined);
                    setShowFrequency(false);
                    setDaysOfMonth([]);
                    setShowSetReminderNotification(true);
                    setSelectedFrequencyPattern(undefined);
                    setManageSubscription(undefined);
                    setSelectedSubscriptionId(null);
                    setTimeout(() => {
                        setShowSetReminderNotification(false);
                    }, 6000);
                    handelGtmTags(
                        "button",
                        "click",
                        "save",
                        "manage my views",
                        "subscribe modal",
                        "subscribe_to_view",
                        selectedFrequencyPattern
                    );
                })
                .catch((e) => {
                    setShowLoader(false);
                    setSelectedRecurrencePattern("");
                    setShowFrequency(false);
                    disableSaveButton = true;
                    setDaysOfMonth([]);
                    setSelectedFrequencyPattern(undefined);
                });
        }
    };

    const getLoaderText = () => {
        switch (actionType) {
            case "Update":
                return "Updating";
            case "Delete":
                return "Deleting";
            case "Set":
                return "Saving";
            case "Share":
                return "Sharing";
            default:
                return "Searching";
        }
    };

    const shareReportNotification = () => {
        setTimeout(() => {
            setShowShareReportNotification(false);
        }, 6000);
    };

    const onShareReportClick = () => {
        setShowShareReportDialogBox(false);
        setActiontype("Share");
        setShowLoader(true);
        saveReportViewMutation({
            dataToUpdate: {
                isfUserId: user.userId,
                ownerType: "Shared",
                selectedIsfOrganizationIds: selectedIsfOrganizationIds,
                dataViewType: selectedView.DataViewType,
                subViewName: selectedView.SubViewName,
                originalViewId: selectedView.ViewId,
                // isEnabled: true, only for PUT
                dataViewInfo: {
                    name: selectedView.Name,
                    description: selectedView.Description,
                    parameters: saveUpdateReportParameters,
                    tableDisplayInformation: {
                        sortOrder:
                            selectedView.TableDisplayInformation.SortOrder,
                        columnInformation:
                            selectedView.TableDisplayInformation.ColumnInformation.map(
                                (item) => {
                                    return {
                                        displayOrder: item.displayOrder,
                                        checked: item.checked,
                                        size: item.size,
                                        columnId: item.columnId,
                                    };
                                }
                            ),
                    },
                },
            },
        }).then(() => {
            getReportDetails(reportDetailsParams).then(() => {
                setShowLoader(false);
                setShowShareReportNotification(true);
                shareReportNotification();
            });
        });
        handelGtmTags(
            "button",
            "click",
            "share",
            "manage my views",
            "share modal",
            "share_view",
            "undefined"
        );
    };

    const sortedTableData = [...tableData].sort((a, b) => {
        const ownerTypeComparison =
            ownerTypeOrder.indexOf(a.OwnerType) -
            ownerTypeOrder.indexOf(b.OwnerType);
        if (ownerTypeComparison !== 0) {
            return ownerTypeComparison;
        } else {
            return (
                dataViewTypeOrder.indexOf(a.DataViewType) -
                dataViewTypeOrder.indexOf(b.DataViewType)
            );
        }
    });

    const handleReportNameClick = (selectedReportData) => {
        navigate(
            `/reportingcapabilities/${
                selectedReportData.DataViewType === "Payments"
                    ? "PaymentHistory"
                    : selectedReportData.DataViewType
            }`
        );

        const selectedDataViewType =
            selectedReportData.DataViewType === "Payments"
                ? "PaymentHistory"
                : selectedReportData.DataViewType;
        dispatch(setSelectedReportViewId(selectedReportData.ViewId));
        dispatch(setSelectedDataViewType(selectedDataViewType));
        const reportName = (selectedReportData.Name || "").toLowerCase();
        const attribute1 =
            selectedReportData.DataViewType === "Payments"
                ? "payment history"
                : `reports ${selectedReportData.DataViewType?.toLowerCase()}`;

        const attribute2 = (
            selectedReportData.OwnerType || "standard"
        ).toLowerCase();
        gtm.Event(
            "",
            "report link",
            "click",
            reportName,
            attribute1,
            attribute2,
            "saved_report_click",
            reportName
        );
    };

    const getReportAction = (item) => {
        if (user.isUserInternal && item.OwnerType !== OWNER_TYPE) return true;
        if (!user.isUserInternal) return true;
    };

    return (
        <>
            <Breadcrumb
                links={[
                    {
                        name: "Reports",
                        path: "/reportingcapabilities",
                    },
                    ...(viewType
                        ? [
                              {
                                  name:
                                      viewType === paymentHistoryType
                                          ? "Payment history"
                                          : viewType,
                                  path: `/reportingcapabilities/${viewType}`,
                              },
                          ]
                        : []),
                    {
                        name: "Manage my reports",
                    },
                ]}
            />

            <div className="section-card px-0  ">
                <div className="d-flex align-items-baseline px-24p">
                    <SectionHeading
                        heading="Manage my reports |"
                        className="mr-10p"
                    />
                    <SectionSubheading
                        className="text-semibold sub-heading-disabled mr-10p"
                        subheading={
                            userSelectedOrganizationReports[reportId]
                                .organization.ORGANIZATIONGROUPNAME
                                ? userSelectedOrganizationReports[reportId]
                                      .organization.ORGANIZATIONGROUPNAME
                                : userSelectedOrganizationReports[reportId]
                                      .organization.ORGNIZATIONNAME
                        }
                    ></SectionSubheading>
                    {userSelectedOrganizationReports[reportId].organization
                        .ORGANIZATIONGROUPNAME ? (
                        <>
                            <SectionHeading heading="|" className="mr-10p" />
                            <div className="sub-heading-disabled-related">
                                <div className="customtooltip">
                                    {"Related orgs: " + formattedRelatedOrgCSV}
                                    <div style={{ marginTop: "16px" }}>
                                        <span className="customTooltipText">
                                            {relatedOrgNames.map(
                                                (relatedOrg) => (
                                                    <>
                                                        <span>
                                                            {relatedOrg}
                                                        </span>
                                                        <br />
                                                    </>
                                                )
                                            )}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </>
                    ) : (
                        <></>
                    )}
                </div>
                {showLoader ? (
                    <Loader text={getLoaderText()} />
                ) : (
                    <>
                        <table className="frequently-used-data">
                            <thead>
                                <tr>
                                    <th
                                        className="py-8p"
                                        style={{ paddingLeft: 24 }}
                                    >
                                        Report name
                                    </th>
                                    <th className="py-8p">Type</th>
                                    <th className="py-8p">Description</th>
                                    {displayManageReminder &&
                                        !user.isUserInternal && (
                                            <th className="py-8p">Reminder</th>
                                        )}
                                    <th className="py-8p">Action</th>
                                </tr>
                            </thead>

                            <tbody>
                                {sortedTableData
                                    .slice(
                                        pagesVisited,
                                        parseInt(pagesVisited) +
                                            parseInt(usersPerPage)
                                    )
                                    .map((item, index) => (
                                        <tr
                                            key={item.ViewId}
                                            className={
                                                index % 2 === 0 ? "odd" : "even"
                                            }
                                        >
                                            <td
                                                className="py-7p"
                                                style={{
                                                    paddingLeft: 24,
                                                }}
                                            >
                                                <span>
                                                    <DataViewIcon
                                                        dataViewType={item}
                                                        onClick={() =>
                                                            handleReportNameClick(
                                                                item
                                                            )
                                                        }
                                                    />
                                                </span>
                                            </td>
                                            <td className="py-7p">
                                                {DOMPurify.sanitize(
                                                    item.OwnerType
                                                )}
                                            </td>
                                            <td className="py-7p">
                                                {DOMPurify.sanitize(
                                                    item.Description
                                                )}
                                            </td>
                                            {!user.isUserInternal && (
                                                <td
                                                    className="py-7p"
                                                    style={{ color: "#037CB7" }}
                                                >
                                                    {(() => {
                                                        const subscriptionInfo =
                                                            item.SubscriptionInfo;
                                                        const frequency =
                                                            subscriptionInfo?.frequency;
                                                        if (
                                                            subscriptionInfo ===
                                                                null ||
                                                            subscriptionInfo ===
                                                                undefined
                                                        ) {
                                                            return (
                                                                <span>
                                                                    &#8212;
                                                                </span>
                                                            );
                                                        }
                                                        if (
                                                            frequency ===
                                                            "Monthly"
                                                        ) {
                                                            return (
                                                                <span className="d-flex align-items-center gap-1">
                                                                    <i
                                                                        className="fa-solid fa-calendar-days"
                                                                        style={{
                                                                            paddingRight: 5,
                                                                        }}
                                                                    ></i>{" "}
                                                                    {frequency}
                                                                </span>
                                                            );
                                                        } else if (
                                                            frequency ===
                                                            "Weekly"
                                                        ) {
                                                            return (
                                                                <span className="d-flex align-items-center gap-1">
                                                                    <i className="fa-solid fa-calendar-week"></i>
                                                                    {frequency}
                                                                </span>
                                                            );
                                                        } else if (
                                                            frequency ===
                                                            "Daily"
                                                        ) {
                                                            return (
                                                                <span className="d-flex align-items-center gap-1">
                                                                    <i className="fa-solid fa-calendar-day"></i>
                                                                    {frequency}
                                                                </span>
                                                            );
                                                        } else {
                                                            return "";
                                                        }
                                                    })()}
                                                </td>
                                            )}
                                            <td
                                                style={{
                                                    paddingLeft: 24,
                                                    paddingTop: 7,
                                                    paddingBottom: 7,
                                                }}
                                            >
                                                <span
                                                    className="cursor-pointer"
                                                    onClick={() =>
                                                        handleActionClick(index)
                                                    }
                                                >
                                                    {getReportAction(item) && (
                                                        <img
                                                            src={ActionIcon}
                                                            alt="action"
                                                        />
                                                    )}
                                                </span>
                                                {selectedAction === index && (
                                                    <Popup
                                                        onClose={() =>
                                                            setSelectedAction(
                                                                null
                                                            )
                                                        }
                                                        onEdit={handleEdit}
                                                        onDelete={handleDelete}
                                                        viewData={item}
                                                        onManageReminder={
                                                            handleManageReminder
                                                        }
                                                        onShare={
                                                            handleShareReport
                                                        }
                                                        displayManageReminder={
                                                            displayManageReminder
                                                        }
                                                    />
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                        {tableData.length > 50 && (
                            <div className="pt-20p">
                                <Pagination
                                    pageCount={getPageCount()}
                                    gotoPage={changePage}
                                    setPageSize={handleItemsPerPageChange}
                                    pageSize={tableItems ?? 50}
                                    options={[50, 100]}
                                    attribute1="report"
                                    pageName="manage my report"
                                    attribute4=""
                                    tabIndex={0}
                                    setPageNumberOnRefresh={pageNumber}
                                />
                            </div>
                        )}
                    </>
                )}
            </div>
            {selectedView && (
                <ReportDeletePage
                    onDontSave={handleReportViewCancelDelete}
                    onSave={handleReportViewDelete}
                    onShow={show}
                    selectedView={selectedView}
                />
            )}
            {isReportDetalisError && <RequestFailed />}
            <SavedReportEditDialogBox
                onShow={showUpdatePage}
                onDontSave={handleReportViewDontSave}
                onSave={handleReportViewUpdate}
                reportName={changedViewName}
                reportDescription={changedViewDescription}
                onViewNameChange={handleViewNameChange}
                onDescriptionChange={handleDescriptionChange}
                selectedView={selectedView}
            />
            <ManageReminderDialogBox
                onShow={showManageReminder}
                onDontSave={handleReportViewDontSave}
                reportName={manageReminderReportName}
                onReminderCheck={handleReminderCheck}
                showFrequency={showFrequency}
                onFrequencyPatternClick={handleFrequencyPatternClick}
                showFrequencyPattern={selectedFrequencyPattern}
                showRecurrencePattern={selectedRecurrencePattern}
                onSave={handleSetReminder}
                onRecurrencePatternClick={handleRecurrencePatternClick}
                handleDaysOfTheWeek={handleDaysOfTheWeek}
                handleMonthly={handleMonthly}
                disableSaveButton={disableSaveButton}
                subscriptionInfo={manageSubscription}
                reminderCheck={selectedReminderCheck}
                daysOfMonth={daysOfMonth}
            />
            <ShareReportDialogBox
                onDontSave={handleReportViewCancelDelete}
                onSave={onShareReportClick}
                onShow={showShareReportDialogBox}
                selectedView={selectedView}
            />
            {showReportSaveNotification && (
                <ReportSaveNotification
                    onCloseNotification={handleReportSavedNotificationClose}
                    msgIndicator="DELETE"
                    selectedView={selectedView}
                ></ReportSaveNotification>
            )}

            {showReportUpdatedNotification && (
                <ReportSaveNotification
                    onCloseNotification={handleReportUpdatedNotificationClose}
                    msgIndicator="manageViewUpdate"
                ></ReportSaveNotification>
            )}
            {showSetReminderNotification && (
                <ReportSaveNotification
                    onCloseNotification={handleSetReminderNotificationClose}
                    msgIndicator="SET"
                ></ReportSaveNotification>
            )}
            {showDeleteReminderNotification && (
                <ReportSaveNotification
                    onCloseNotification={handleDeleteReminderNotificationClose}
                    msgIndicator="Del"
                ></ReportSaveNotification>
            )}
            {showUpdateReminderNotification && (
                <ReportSaveNotification
                    onCloseNotification={handleUpdateReminderNotificationClose}
                    msgIndicator="PUT"
                ></ReportSaveNotification>
            )}
            {showShareReportNotification && (
                <ReportSaveNotification
                    onCloseNotification={handleShareReportNotificationClose}
                    msgIndicator="manageViewUpdate"
                    titleInformation="reportShared"
                ></ReportSaveNotification>
            )}
        </>
    );
};

Popup.propTypes = {
    onClose: PropTypes.func.isRequired,
    onEdit: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    viewData: PropTypes.shape({
        OwnerType: PropTypes.string.isRequired,
        Shared: PropTypes.bool,
    }).isRequired,
    onManageReminder: PropTypes.func,
    displayManageReminder: PropTypes.bool,
    onShare: PropTypes.func,
};

export default ManageMyView;
